import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { ProposalStatus, FilterStatus } from './proposal-filter.js';

/**
 * A single search result item as returned by the search API.
 */
export interface SearchResultItem {
  issue_number: number;
  title: string;
  current_status: ProposalStatus;
  previous_status?: ProposalStatus | null;
  week: string;
  changed_at: string;
  url: string;
  summary: string;
  score: number;
  vector_score?: number;
}

/**
 * Successful search API response shape.
 */
export interface SearchResponse {
  query: string;
  reranked: boolean;
  results: SearchResultItem[];
}

/**
 * Error API response shape (non-2xx).
 */
export interface SearchErrorResponse {
  error: string;
}

/**
 * Status labels for display, duplicated from proposal-filter.ts (not
 * exported there) to keep the two components independently deployable.
 * Keep in sync with STATUS_LABELS in proposal-filter.ts.
 */
const STATUS_LABELS: Record<ProposalStatus, string> = {
  accepted: 'Accepted',
  declined: 'Declined',
  likely_accept: 'Likely Accept',
  likely_decline: 'Likely Decline',
  discussions: 'Discussions',
  active: 'Active',
  hold: 'Hold',
};

/**
 * Order in which statuses are offered in the filter select, per spec.
 */
const STATUS_OPTIONS: ProposalStatus[] = [
  'accepted',
  'likely_accept',
  'likely_decline',
  'active',
  'hold',
  'declined',
  'discussions',
];

const VALID_STATUSES: ReadonlySet<string> = new Set(STATUS_OPTIONS);

function isValidStatus(value: string | null): value is ProposalStatus {
  return value !== null && VALID_STATUSES.has(value);
}

/**
 * Lifecycle phase of the search UI.
 */
type SearchPhase = 'idle' | 'loading' | 'error' | 'results';

/**
 * ProposalSearch is a Lit web component providing a search form over the
 * proposal-summary search API and rendering the results inline.
 *
 * @example
 * ```html
 * <proposal-search api="/api/search"></proposal-search>
 * ```
 */
@customElement('proposal-search')
export class ProposalSearch extends LitElement {
  static styles = css`
    :host {
      display: block;
      font-family: 'Noto Sans JP', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      color: var(--text-primary, #1f2937);
    }

    form {
      display: flex;
      flex-wrap: wrap;
      gap: 0.75rem;
      align-items: stretch;
    }

    .search-input {
      flex: 1 1 16rem;
      min-width: 0;
      padding: 0.75rem 1rem;
      border: 2px solid var(--border-color, #e5e7eb);
      border-radius: 0.5rem;
      background-color: var(--bg-card, white);
      color: var(--text-primary, #1f2937);
      font-size: 0.9375rem;
      font-family: inherit;
    }

    .search-input:focus {
      outline: none;
      border-color: var(--go-blue, #00acd7);
      box-shadow: 0 0 0 3px rgba(0, 172, 215, 0.1);
    }

    .status-select {
      flex: 0 1 12rem;
      padding: 0.75rem 1rem;
      border: 2px solid var(--border-color, #e5e7eb);
      border-radius: 0.5rem;
      background-color: var(--bg-card, white);
      color: var(--text-primary, #1f2937);
      font-size: 0.9375rem;
      font-family: inherit;
    }

    .status-select:focus {
      outline: none;
      border-color: var(--go-blue, #00acd7);
      box-shadow: 0 0 0 3px rgba(0, 172, 215, 0.1);
    }

    .submit-button {
      flex: 0 0 auto;
      padding: 0.75rem 1.5rem;
      border: none;
      border-radius: 0.5rem;
      background-color: var(--go-blue, #00acd7);
      color: var(--text-on-blue, white);
      font-size: 0.9375rem;
      font-weight: 600;
      font-family: inherit;
      cursor: pointer;
      transition: background-color 0.2s;
    }

    .submit-button:hover {
      background-color: var(--go-blue-dark, #007d9c);
    }

    .submit-button:disabled {
      cursor: not-allowed;
      opacity: 0.6;
    }

    .results {
      margin-top: 1.5rem;
    }

    .status-message {
      color: var(--text-muted, #6b7280);
      font-size: 0.9375rem;
    }

    .status-message.error {
      color: #b91c1c;
    }

    ul.result-list {
      list-style: none;
      margin: 0;
      padding: 0;
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }

    .result-item {
      padding: 1rem;
      border: 1px solid var(--border-color, #e5e7eb);
      border-radius: 0.5rem;
      background-color: var(--bg-card, white);
    }

    .result-header {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 0.5rem;
      margin-bottom: 0.375rem;
    }

    .result-title {
      font-weight: 600;
      color: var(--go-blue, #00acd7);
      text-decoration: none;
    }

    .result-title:hover {
      text-decoration: underline;
    }

    .issue-number {
      color: var(--text-muted, #6b7280);
      font-size: 0.875rem;
    }

    .badge {
      display: inline-flex;
      align-items: center;
      padding: 0.125rem 0.5rem;
      border-radius: 0.25rem;
      font-size: 0.75rem;
      font-weight: 500;
    }

    .badge-accepted { background-color: #dcfce7; color: #166534; }
    .badge-declined { background-color: #fee2e2; color: #991b1b; }
    .badge-likely_accept { background-color: #d1fae5; color: #065f46; }
    .badge-likely_decline { background-color: #ffedd5; color: #9a3412; }
    .badge-discussions { background-color: #f3e8ff; color: #6b21a8; }
    .badge-active { background-color: #e0f2fe; color: #075985; }
    .badge-hold { background-color: #fef9c3; color: #854d0e; }

    .week-label {
      color: var(--text-muted, #6b7280);
      font-size: 0.8125rem;
    }

    .summary {
      margin: 0.5rem 0 0.375rem;
      color: var(--text-secondary, #374151);
      font-size: 0.875rem;
      line-height: 1.5;
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 3;
      overflow: hidden;
    }

    .relevance {
      color: var(--text-muted, #9ca3af);
      font-size: 0.75rem;
    }
  `;

  /**
   * URL of the search API endpoint.
   */
  @property({ type: String })
  api = '/api/search';

  /**
   * Current value of the search text input.
   */
  @state()
  private query = '';

  /**
   * Currently selected status filter. 'all' means no filter.
   */
  @state()
  private selectedStatus: FilterStatus = 'all';

  @state()
  private phase: SearchPhase = 'idle';

  @state()
  private results: SearchResultItem[] = [];

  @state()
  private errorMessage = '';

  private controller: AbortController | null = null;

  connectedCallback(): void {
    super.connectedCallback();
    const params = new URLSearchParams(window.location.search);
    const q = params.get('q');
    if (q && q.trim()) {
      this.query = q;
      const statusParam = params.get('status');
      this.selectedStatus = isValidStatus(statusParam) ? statusParam : 'all';
      void this.performSearch(this.query, this.selectedStatus);
    }
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.controller?.abort();
    this.controller = null;
  }

  /**
   * Get the display label for a status.
   */
  private getStatusLabel(status: ProposalStatus): string {
    return STATUS_LABELS[status] || status;
  }

  private handleQueryInput(event: Event): void {
    this.query = (event.target as HTMLInputElement).value;
  }

  private handleStatusChange(event: Event): void {
    const value = (event.target as HTMLSelectElement).value;
    this.selectedStatus = isValidStatus(value) ? value : 'all';
  }

  private handleSubmit(event: Event): void {
    event.preventDefault();
    void this.performSearch(this.query, this.selectedStatus);
  }

  /**
   * Sync the current query/status into the URL via history.replaceState,
   * without adding a new history entry.
   */
  private syncUrl(query: string, status: FilterStatus): void {
    if (typeof window === 'undefined' || !window.history) return;
    const url = new URL(window.location.href);
    if (query) {
      url.searchParams.set('q', query);
    } else {
      url.searchParams.delete('q');
    }
    if (status !== 'all') {
      url.searchParams.set('status', status);
    } else {
      url.searchParams.delete('status');
    }
    window.history.replaceState(window.history.state, '', url.toString());
  }

  /**
   * Perform a search request against the API, aborting any in-flight
   * request first. Exported behavior only; not part of the public API.
   */
  private async performSearch(rawQuery: string, status: FilterStatus): Promise<void> {
    const trimmed = rawQuery.trim();
    if (!trimmed) {
      return;
    }

    this.controller?.abort();
    const controller = new AbortController();
    this.controller = controller;

    this.phase = 'loading';
    this.errorMessage = '';

    this.syncUrl(trimmed, status);

    const params = new URLSearchParams();
    params.set('q', trimmed);
    if (status !== 'all') {
      params.set('status', status);
    }

    try {
      const response = await fetch(`${this.api}?${params.toString()}`, {
        signal: controller.signal,
      });

      if (controller.signal.aborted) return;

      let body: unknown = null;
      try {
        body = await response.json();
      } catch {
        body = null;
      }

      if (controller.signal.aborted) return;

      if (!response.ok) {
        const message =
          body && typeof body === 'object' && typeof (body as SearchErrorResponse).error === 'string'
            ? (body as SearchErrorResponse).error
            : '検索中にエラーが発生しました。';
        this.errorMessage = message;
        this.phase = 'error';
        return;
      }

      const data = (body ?? {}) as Partial<SearchResponse>;
      this.results = Array.isArray(data.results) ? data.results : [];
      this.phase = 'results';
    } catch (err) {
      if (controller.signal.aborted || (err instanceof DOMException && err.name === 'AbortError')) {
        return;
      }
      this.errorMessage = 'ネットワークエラーが発生しました。時間をおいて再度お試しください。';
      this.phase = 'error';
    }
  }

  /**
   * Focuses the search text input inside this component's shadow DOM.
   * Public API used by web/components/search-shortcut.ts so the global "/"
   * keyboard shortcut can reach into this component on the /search/ page,
   * where there is no plain nav/hero `<input>` in the light DOM to target.
   */
  async focusInput(): Promise<void> {
    await this.updateComplete;
    this.renderRoot.querySelector<HTMLInputElement>('.search-input')?.focus();
  }

  private relevancePercent(item: SearchResultItem): number {
    const raw = typeof item.score === 'number' ? item.score : (item.vector_score ?? 0);
    return Math.round(Math.min(1, Math.max(0, raw)) * 100);
  }

  private renderResultItem(item: SearchResultItem) {
    return html`
      <li class="result-item">
        <div class="result-header">
          <a class="result-title" href="${item.url}">${item.title}</a>
          <span class="issue-number">#${item.issue_number}</span>
          <span class="badge badge-${item.current_status}">${this.getStatusLabel(item.current_status)}</span>
          <span class="week-label">${item.week}</span>
        </div>
        <p class="summary">${item.summary}</p>
        <p class="relevance">関連度 ${this.relevancePercent(item)}%</p>
      </li>
    `;
  }

  private renderResults() {
    if (this.phase === 'loading') {
      return html`<p class="status-message">検索中…</p>`;
    }

    if (this.phase === 'error') {
      return html`<p class="status-message error" role="alert">${this.errorMessage}</p>`;
    }

    if (this.phase === 'results') {
      if (this.results.length === 0) {
        return html`<p class="status-message">該当するプロポーザルが見つかりませんでした</p>`;
      }
      return html`
        <ul class="result-list">
          ${this.results.map((item) => this.renderResultItem(item))}
        </ul>
      `;
    }

    return nothing;
  }

  render() {
    return html`
      <form @submit="${this.handleSubmit}">
        <input
          class="search-input"
          type="search"
          name="q"
          .value="${this.query}"
          @input="${this.handleQueryInput}"
          placeholder="例: iterator の追加、context のキャンセル…"
          maxlength="200"
          aria-label="検索キーワード"
        />
        <select class="status-select" @change="${this.handleStatusChange}" aria-label="ステータスで絞り込み">
          <option value="all" ?selected="${this.selectedStatus === 'all'}">すべて</option>
          ${STATUS_OPTIONS.map(
            (status) => html`
              <option value="${status}" ?selected="${this.selectedStatus === status}">
                ${this.getStatusLabel(status)}
              </option>
            `,
          )}
        </select>
        <button class="submit-button" type="submit" ?disabled="${this.phase === 'loading'}">検索</button>
      </form>
      <div class="results" aria-live="polite" aria-busy="${this.phase === 'loading'}">${this.renderResults()}</div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'proposal-search': ProposalSearch;
  }
}
